// src/services/operationCenter/DashboardAIService.ts
// AI service for generating dashboard widgets from natural language prompts
// All prompt generation and AI logic is handled by the backend

import { API_BASE_URL } from "@/services";
import type { WidgetType, WidgetConfig } from "@/types/dashboard";
import { getValidWidgetTypes, getWidgetSchema } from "./WidgetSchemaService";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface AIWidgetSpec {
  type: WidgetType;
  title: string;
  config: WidgetConfig;
}

export interface AIDashboardSpec {
  name: string;
  description?: string;
  widgets: AIWidgetSpec[];
}

export interface AIQueryResponse {
  content: string;
  timestamp: string;
}

/**
 * Thesis context for AI widget generation - sent to backend
 */
export interface ThesisContext {
  title: string;
  description: string;
  conditions: Array<{
    type: string;
    label?: string;
    metricKey?: string;
    entityId?: string;
    operator?: string;
    threshold?: number;
    prompt?: string;
    timeframe?: string;
  }>;
  status: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Backend API Base URL
// ─────────────────────────────────────────────────────────────────────────────

const AI_API_BASE = API_BASE_URL || "http://localhost:8000";

// ─────────────────────────────────────────────────────────────────────────────
// Service Class
// ─────────────────────────────────────────────────────────────────────────────

export class DashboardAIService {
  /**
   * Generate widget specifications from a natural language prompt.
   * All prompt generation and AI logic is handled by the backend.
   */
  async generateWidgets(prompt: string): Promise<AIWidgetSpec[]> {
    const response = await fetch(`${AI_API_BASE}/workspace/suggestWidgets`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: prompt,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        `AI API error: ${response.status} - ${error.message || error.detail || "Unknown error"}`,
      );
    }

    const data = await response.json();
    const widgets = data.widgets || [];

    return this.normalizeWidgets(widgets);
  }

  /**
   * Generate a complete dashboard from a natural language description.
   * All prompt generation and AI logic is handled by the backend.
   * @param prompt - Natural language description of the desired dashboard
   * @param _thesis - Optional thesis context to inform widget generation
   */
  async generateDashboard(
    prompt: string,
    _thesis?: ThesisContext,
  ): Promise<AIDashboardSpec> {
    // Store thesis for future use when backend supports it
    void _thesis;
    const response = await fetch(`${AI_API_BASE}/workspace/suggestWidgets`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: prompt,
        // thesis: _thesis || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        `AI API error: ${response.status} - ${error.message || error.detail || "Unknown error"}`,
      );
    }

    const data = await response.json();
    const name = data.name || "New Dashboard";
    const widgets = this.normalizeWidgets(data.widgets || []);

    return {
      name,
      description:
        data.description || `AI-generated dashboard: ${prompt.slice(0, 100)}`,
      widgets,
    };
  }

  /**
   * Get an AI response for the AI Query widget.
   * All prompt generation and AI logic is handled by the backend.
   */
  async queryAI(prompt: string, context?: string): Promise<AIQueryResponse> {
    const response = await fetch(`${AI_API_BASE}/api/ai/workspace/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: prompt,
        context: context || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        `AI API error: ${response.status} - ${error.message || error.detail || "Unknown error"}`,
      );
    }

    const data = await response.json();
    return {
      content: data.content,
      timestamp: data.timestamp || new Date().toISOString(),
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Normalize widgets received from backend - applies default configs from frontend schema
   */
  private normalizeWidgets(widgets: AIWidgetSpec[]): AIWidgetSpec[] {
    const validTypes = getValidWidgetTypes();

    return widgets
      .filter((w: AIWidgetSpec) => w.type && validTypes.has(w.type))
      .map((w: AIWidgetSpec) => {
        const schema = getWidgetSchema(w.type);
        return {
          type: w.type,
          title: w.title || schema?.label || "Widget",
          config: {
            ...schema?.defaultConfig,
            ...w.config,
          },
        };
      });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Singleton Instance
// ─────────────────────────────────────────────────────────────────────────────

let serviceInstance: DashboardAIService | null = null;

export function getDashboardAIService(): DashboardAIService {
  if (!serviceInstance) {
    serviceInstance = new DashboardAIService();
  }
  return serviceInstance;
}

/**
 * Check if AI capability is available by calling the backend status endpoint
 */
export async function hasOpenAIApiKey(): Promise<boolean> {
  try {
    const response = await fetch(`${AI_API_BASE}/api/ai/status`);
    if (!response.ok) return false;
    const data = await response.json();
    return data.available === true;
  } catch {
    return false;
  }
}

/**
 * Convert a Thesis object to ThesisContext for AI generation
 */
export function thesisToContext(thesis: {
  title: string;
  description: string;
  status: string;
  conditions: Array<{
    type: string;
    label?: string;
    metricKey?: string;
    entityId?: string;
    operator?: string;
    threshold?: number;
    prompt?: string;
    timeframe?: string;
  }>;
}): ThesisContext {
  return {
    title: thesis.title,
    description: thesis.description,
    status: thesis.status,
    conditions: thesis.conditions.map((c) => {
      const condition: ThesisContext["conditions"][number] = { type: c.type };
      if (c.label) condition.label = c.label;
      if (c.metricKey) condition.metricKey = c.metricKey;
      if (c.entityId) condition.entityId = c.entityId;
      if (c.operator) condition.operator = c.operator;
      if (c.threshold !== undefined) condition.threshold = c.threshold;
      if (c.prompt) condition.prompt = c.prompt;
      if (c.timeframe) condition.timeframe = c.timeframe;
      return condition;
    }),
  };
}

export default DashboardAIService;
